/**
 * Diseño y reglas reutilizables para notificaciones móviles (FCM, canal local, Web Push).
 *
 * Objetivo: un solo lugar donde definir categorías, canales, prioridad y plantillas de copy
 * para que, al conectar push real, solo se arme el payload con estos tipos y constantes.
 *
 * REGLAS (mantener al evolucionar el producto):
 * - channelId: estable en el tiempo; en Android cambiarlo rompe preferencias del usuario en ese canal.
 * - category: alineada con preferencias en UI (ventas, inventario, personal, sistema).
 * - priority "max" solo para seguridad / fraude / pérdida de datos; el resto default o high.
 * - title/body: cortos; el cuerpo puede tener una segunda línea en "rich" / bigText.
 * - data: solo strings (requisito típico FCM); IDs y deep links van aquí.
 * - Marca: usar ALTENOT_NOTIFICATION_BRAND para acento e icono; no hardcodear hex fuera de este archivo.
 */

export const ALTENOT_NOTIFICATION_BRAND = {
  appName: "ALTENOT",
  /** Color acento por defecto (canal "marketing"/genérico) */
  colorAccent: "#2563eb",
  /**
   * Ícono pequeño universal (badge de la barra de estado): el logo de la
   * app (silueta de casa), igual para todas las categorías — el color de
   * acento por canal es lo que las distingue ahí. Vive en res/drawable
   * porque Android solo pinta siluetas monocromas en este espacio.
   */
  smallIconResource: "ic_stat_altenot",
} as const;

/** ID de la acción rápida "Marcar como leído" registrada una vez al conceder permiso. */
export const AUDIT_EVENT_ACTION_TYPE_ID = "ALTENOT_AUDIT_EVENT";
export const MARK_AS_READ_ACTION_ID = "mark_read";
/** Texto del botón — única fuente de verdad para no duplicar el literal. */
export const MARK_AS_READ_ACTION_LABEL = "Marcar como leído";

export type NotificationCategory =
  | "ventas"
  | "inventario"
  | "personal"
  | "sistema"
  | "marketing";

export type NotificationPriority = "default" | "high" | "max";

/** Payload lógico: el servicio nativo o FCM mapea esto a NotificationCompat / APNs. */
export interface MobileNotificationPayload {
  channelId: string;
  category: NotificationCategory;
  title: string;
  body: string;
  /** Identificador de plantilla para analytics y A/B futuros */
  templateId?: string;
  priority?: NotificationPriority;
  /** FCM data: valores string */
  data?: Record<string, string>;
  /** Android: ícono pequeño universal — logo de la app (res/drawable, sin extensión). */
  smallIcon?: string;
  /** Android: color de acento de la categoría (tinta el badge pequeño + el canal). */
  iconColor?: string;
  /**
   * Android: ícono grande a color en la esquina — el glifo de la categoría
   * (carrito, caja, personas, alerta) sobre un cuadrado de color, igual al
   * estilo "Modo Tarjeta" de Auditoría. Debe ser un PNG (res/drawable): el
   * plugin resuelve `largeIcon` con `BitmapFactory`, que NO soporta vectores.
   */
  largeIcon?: string;
  /** ID de tipo de acción registrado (botones rápidos, ej. "Marcar como leído"). */
  actionTypeId?: string;
  /** Texto del botón de acción rápida (Android nativo lo necesita literal, no solo el ID). */
  actionLabel?: string;
  /** ID numérico fijo (opcional) — mismo id = se actualiza in-place en vez de acumularse. */
  id?: number;
}

/** Reglas por canal Android (id + nombre visible + descripción; importancia implícita en priority). */
export const NOTIFICATION_CHANNEL_RULES: Record<
  NotificationCategory,
  {
    channelId: string;
    channelName: string;
    channelDescription: string;
    defaultPriority: NotificationPriority;
    /** Color de acento Android (tinta el badge pequeño + LED del canal). */
    color: string;
    /**
     * PNG a color de la categoría (carrito/caja/personas/alerta sobre fondo
     * de color) — es el `largeIcon`, el elemento visual PRINCIPAL de la
     * notificación, igual al "Modo Tarjeta" de Auditoría.
     */
    largeIconResource: string;
  }
> = {
  ventas: {
    channelId: "altenot_ventas",
    channelName: "Ventas",
    channelDescription: "Ventas, caja y pedidos",
    defaultPriority: "high",
    color: "#2563eb",
    largeIconResource: "ic_notif_large_ventas",
  },
  inventario: {
    channelId: "altenot_inventario",
    channelName: "Inventario",
    channelDescription: "Stock y alertas de productos",
    defaultPriority: "high",
    color: "#d97706",
    largeIconResource: "ic_notif_large_inventario",
  },
  personal: {
    channelId: "altenot_personal",
    channelName: "Personal",
    channelDescription: "Asistencia y equipo",
    defaultPriority: "default",
    color: "#7c3aed",
    largeIconResource: "ic_notif_large_personal",
  },
  sistema: {
    channelId: "altenot_sistema",
    channelName: "Sistema",
    channelDescription: "Actualizaciones y alertas críticas",
    defaultPriority: "high",
    color: "#e11d48",
    largeIconResource: "ic_notif_large_sistema",
  },
  marketing: {
    channelId: "altenot_marketing",
    channelName: "Novedades",
    channelDescription: "Tips y novedades del producto",
    defaultPriority: "default",
    color: "#2563eb",
    largeIconResource: "ic_notif_large_sistema",
  },
};

const TEMPLATE_IDS = {
  prefsActivated: "altenot.prefs.notifications.activated",
} as const;

/** Ejemplo de payload cuando el usuario activa notificaciones (preview + tests futuros). */
export function buildExampleActivationNotification(): MobileNotificationPayload {
  const rule = NOTIFICATION_CHANNEL_RULES.sistema;
  return {
    channelId: rule.channelId,
    category: "sistema",
    title: `Activaste las notificaciones de ${ALTENOT_NOTIFICATION_BRAND.appName}`,
    body: "Te avisaremos de ventas, inventario y alertas según lo que tengas encendido en Ajustes. Puedes cambiarlo cuando quieras.",
    templateId: TEMPLATE_IDS.prefsActivated,
    priority: "default",
    data: {
      screen: "settings/notifications",
      action: "prefs_updated",
    },
  };
}

/**
 * Ensambla un payload mínimo para una categoría concreta (uso desde servidor o cliente).
 * `priorityOverride` permite que el llamador (ej. auditoría) suba la prioridad
 * según la severidad real del evento en vez de usar siempre el default del canal.
 * El ícono grande (categoría, a color), el color de acento y el canal se
 * derivan siempre de `NOTIFICATION_CHANNEL_RULES` — nunca se hardcodean fuera
 * de este archivo. Cada notificación se muestra INDIVIDUAL (sin agrupar):
 * el usuario debe poder ver y descartar cada evento por separado.
 */
export function buildCategoryNotification(
  category: NotificationCategory,
  title: string,
  body: string,
  data?: Record<string, string>,
  priorityOverride?: NotificationPriority,
): MobileNotificationPayload {
  const rule = NOTIFICATION_CHANNEL_RULES[category];
  return {
    channelId: rule.channelId,
    category,
    title,
    body,
    priority: priorityOverride ?? rule.defaultPriority,
    data,
    smallIcon: ALTENOT_NOTIFICATION_BRAND.smallIconResource,
    iconColor: rule.color,
    largeIcon: rule.largeIconResource,
    actionTypeId: AUDIT_EVENT_ACTION_TYPE_ID,
    actionLabel: MARK_AS_READ_ACTION_LABEL,
  };
}
